import { memo, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import {
  ArrowForwardIcon,
  CallReceivedIcon,
  CallSharedIcon,
  CarIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  SMSIcon,
  SMSReceivedIcon,
  SMSSharedIcon,
  LandlineIcon,
  SmartphoneIcon,
  VOIPIcon,
  MarkerIcon,
} from '@rsos/assets/icons';
import { Absolute, Box } from '@rsos/components/capstone/base';
import {
  CALL_REROUTED,
  RTI_DOWNSTREAM_SHARE,
} from '@rsos/constants/collaborator';
import {
  VOIP,
  PHASE,
  TEXT,
  ALI_UNDERSCORE,
} from '@rsos/constants/locationTypes';
import { QUEUE_FIXED_WIDTH } from '@rsos/constants/queueWidths';
import { getCollaboratorTypeDisplayName } from './helpers';

// Queue width minus twice the icon's 22px left inset (EmergencyCall row
// padding), so the tooltip's edges sit equally inset within the queue.
const QUEUE_ROW_LEFT_INSET = 22;
const RTI_TOOLTIP_WIDTH = `${QUEUE_FIXED_WIDTH - QUEUE_ROW_LEFT_INSET * 2}px`;

// Width lives on the Absolute wrapper (this bubble just fills it at 100%)
// because the wrapper's containing block is the icon's own tiny box.
const RtiTooltipBubble = styled(Box).attrs({
  padding: '4px',
  backgroundColor: 'primaryText',
  color: 'background',
})`
  border-radius: 5px;
  font-size: 12px;
  word-break: break-word;
  width: 100%;
`;

export const EmergencyCallIcons = props => {
  const theme = useContext(ThemeContext);
  const [iconType, setIconType] = useState('call');
  const [isRtiTooltipVisible, setIsRtiTooltipVisible] = useState(false);
  const {
    isPremiumUser,
    applicationType,
    classOfService,
    isAdmin,
    collaboratorType,
    pairedAccountName,
    isFord = false,
  } = props;

  const resolveCallIcon = () => {
    const isAniAli = applicationType?.startsWith(ALI_UNDERSCORE);
    // Takes precedence over every other card treatment below.
    if (collaboratorType === RTI_DOWNSTREAM_SHARE) {
      return (
        <Box
          position="relative"
          onMouseEnter={() => setIsRtiTooltipVisible(true)}
          onMouseLeave={() => setIsRtiTooltipVisible(false)}
        >
          <ArrowForwardIcon
            width={16}
            height={12}
            color={theme.colors.lightWarning}
          />
          {isRtiTooltipVisible && (
            <Absolute
              top="calc(100% + 4px)"
              left={0}
              width={RTI_TOOLTIP_WIDTH}
              zIndex={10}
            >
              <RtiTooltipBubble>
                {getCollaboratorTypeDisplayName(
                  collaboratorType,
                  pairedAccountName,
                )}
              </RtiTooltipBubble>
            </Absolute>
          )}
        </Box>
      );
    }
    if (isFord) return <CarIcon width={24} height={24} />;
    if (isAdmin) return <SmartphoneIcon width={24} height={16} />;
    if (collaboratorType === CALL_REROUTED)
      return <MarkerIcon width={24} height={16} />;
    if (classOfService === TEXT) return <SMSIcon width={24} height={16} />;
    if (isPremiumUser) {
      if (
        isAniAli &&
        ((classOfService && !classOfService?.startsWith(PHASE)) ||
          !classOfService)
      ) {
        if (classOfService === VOIP) {
          return (
            <VOIPIcon
              width={24}
              height={16}
              viewboxWidth={16}
              viewboxHeight={16}
            />
          );
        }
        return <LandlineIcon width={24} height={16} />;
      }
      return <SmartphoneIcon width={24} height={16} />;
    }
    return (
      <PhoneIcon width={24} height={16} viewboxWidth={11} viewboxHeight={15} />
    );
  };

  const getIcons = type => {
    const iconUnseenColor = theme.colors.queue.emergencyCall.unseen;
    const icons = {
      call: resolveCallIcon(),
      callReceived: <CallReceivedIcon width={24} height={16} />,
      callReceivedUnseen: (
        <CallReceivedIcon width={24} height={16} color={iconUnseenColor} />
      ),
      callShared: <CallSharedIcon width={24} height={16} />,
      search: <MagnifyingGlassIcon width={24} height={16} />,
      sms: <SMSIcon width={24} height={16} />,
      smsReceived: <SMSReceivedIcon width={25} height={25} />,
      smsReceivedUnseen: (
        <SMSReceivedIcon width={25} height={25} color={iconUnseenColor} />
      ),
      smsShared: <SMSSharedIcon width={25} height={26} />,
    };

    return icons[type];
  };

  const checkIconType = status => {
    const {
      isManualSearch,
      isSMSReceived,
      isSMSShared,
      isSMS,
      isCallReceived,
      isCallShared,
      isUnseen,
    } = status;
    if (isManualSearch) return 'search';
    else if (isSMSReceived)
      return isUnseen ? 'smsReceivedUnseen' : 'smsReceived';
    else if (isSMSShared) return 'smsShared';
    else if (isCallReceived)
      return isUnseen ? 'callReceivedUnseen' : 'callReceived';
    else if (isCallShared) return 'callShared';
    else if (isSMS) return 'sms';
    else return 'call';
  };

  useEffect(() => {
    setIconType(checkIconType(props));
  }, [props]);

  return <>{getIcons(iconType)}</>;
};

EmergencyCallIcons.propTypes = {
  isManualSearch: PropTypes.bool,
  isCallReceived: PropTypes.bool,
  isCallShared: PropTypes.bool,
  isSMS: PropTypes.bool,
  isSMSReceived: PropTypes.bool,
  isSMSShared: PropTypes.bool,
  isUnseen: PropTypes.bool,
  isFord: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isPremiumUser: PropTypes.bool,
  applicationType: PropTypes.string,
  classOfService: PropTypes.string,
  collaboratorType: PropTypes.string,
  pairedAccountName: PropTypes.string,
};

export default memo(EmergencyCallIcons);
